import { jwtDecode } from "jwt-decode";
import { apiGatewayClient } from "../api/clients";

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = "auth_tokens";
const REFRESH_THRESHOLD_SECONDS = 5 * 60;
const REFRESH_MAX_RETRIES = 2;
const REFRESH_RETRY_BASE_MS = 500;

let refreshPromise: Promise<AuthTokens | null> | null = null;

type TokenChangeListener = (tokens: AuthTokens | null) => void;

const listeners = new Set<TokenChangeListener>();

export function subscribeToTokenChanges(listener: TokenChangeListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(tokens: AuthTokens | null): void {
  listeners.forEach((listener) => {
    try {
      listener(tokens);
    } catch (error) {
      console.error({ msg: "tokenManager listener error", error });
    }
  });
}

export function getStoredTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return null;

    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

export function setStoredTokens(tokens: AuthTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  notifyListeners(tokens);
}

export function clearStoredTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
  notifyListeners(null);
}

export function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);

    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

function isTokenExpiringSoon(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);

    return Date.now() >= (exp - REFRESH_THRESHOLD_SECONDS) * 1000;
  } catch {
    return true;
  }
}

function isAuthError(error: unknown): boolean {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: string } };
    }
  )?.response;

  const status = response?.status;
  const message = response?.data?.message ?? "";
  const isUnauthorized = status === 401 || status === 403;

  const isExpiredRefreshToken =
    status === 400 && /refresh token.*(expired|revoked|invalid)/i.test(message);

  return isUnauthorized || isExpiredRefreshToken;
}

async function refreshTokens(refreshToken: string): Promise<AuthTokens | null> {
  const currentRefreshToken = refreshToken;

  for (let attempt = 0; attempt <= REFRESH_MAX_RETRIES; attempt++) {
    try {
      const response = await apiGatewayClient.post<{
        payload: {
          idToken: string;
          accessToken: string;
          refreshToken?: string;
        };
      }>("/auth/refresh-session", { refreshToken: currentRefreshToken });

      const {
        idToken,
        accessToken,
        refreshToken: rotatedRefreshToken,
      } = response.data.payload;

      const updated: AuthTokens = {
        idToken,
        accessToken,
        refreshToken: rotatedRefreshToken ?? currentRefreshToken,
      };

      setStoredTokens(updated);

      return updated;
    } catch (error) {
      if (isAuthError(error)) {
        clearStoredTokens();

        return null;
      }

      if (attempt === REFRESH_MAX_RETRIES) {
        console.error({ msg: "Token refresh failed after retries", error });

        return null;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, REFRESH_RETRY_BASE_MS * Math.pow(2, attempt)),
      );
    }
  }

  return null;
}

export async function getValidToken(): Promise<string | null> {
  const tokens = getStoredTokens();

  if (!tokens) return null;

  if (!isTokenExpiringSoon(tokens.idToken)) {
    return tokens.idToken;
  }

  if (!refreshPromise) {
    refreshPromise = refreshTokens(tokens.refreshToken).finally(() => {
      refreshPromise = null;
    });
  }

  const refreshed = await refreshPromise;

  return refreshed?.idToken ?? null;
}
